<script>
  import Icon from "@iconify/svelte";
  import closeIcon from "@iconify/icons-mdi/close";
  import deleteIcon from "@iconify/icons-mdi/delete-outline";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import saveIcon from "@iconify/icons-mdi/content-save";
  import clockIcon from "@iconify/icons-mdi/clock-outline";
  import hljs from "highlight.js";
  import "highlight.js/styles/atom-one-dark.css";

  import { getApiErrorMessage } from "./lib/apiError.js";
  import { t } from "./lib/i18n.js";

  export let apiBase = "";
  export let browseBase = ""; // base for key API e.g. /browse/hostId
  export let apiKey = "";
  export let locale = "en";
  export let selectedDb = 0;
  export let key = "";
  export let detail = null;

  $: keyApiBase = browseBase || apiBase + "/browse/localhost%3A6379";
  export let onClose = () => {};
  export let onDelete = () => {};
  export let onRefresh = () => {};
  export let onUnauthorized = () => {};

  let editValue = "";
  let editTTL = 0;
  let saving = false;
  let error = "";

  $: if (detail?.type === "string" && typeof detail.value === "string") {
    editValue = detail.value;
  } else if (detail?.value) {
    editValue = JSON.stringify(detail.value, null, 2);
  }

  $: if (detail?.ttl > 0) {
    editTTL = detail.ttl;
  } else {
    editTTL = 0;
  }

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  async function saveKey() {
    saving = true;
    error = "";

    try {
      const url = `${keyApiBase}/db${selectedDb}/key/${encodeURIComponent(key)}`;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...authHeaders()
        },
        body: JSON.stringify({
          value: editValue,
          ttl: editTTL > 0 ? editTTL : 0
        })
      });

      if (res.status === 401) {
        onUnauthorized(t(locale, "session_expired"));
        return;
      }

      if (res.status === 204) {
        onRefresh();
        return;
      }
      if (!res.ok) {
        error = await getApiErrorMessage(res, locale);
        return;
      }
    } catch (e) {
      error = e?.message || String(e);
    } finally {
      saving = false;
    }
  }

  function formatTTL(seconds) {
    if (seconds < 0) return t(locale, "browse_no_expiry");
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
  }

  function getTypeBadgeClass(type) {
    const classes = {
      string: "type-string",
      list: "type-list",
      set: "type-set",
      zset: "type-zset",
      hash: "type-hash",
      stream: "type-stream"
    };
    return classes[type] || "type-module";
  }

  /** Detect value format for string keys: JSON, PHP serialized, or plain */
  function getValueFormat(val) {
    if (val == null || typeof val !== "string") return t(locale, "detail_plain");
    const s = val.trim();
    if (s.length === 0) return t(locale, "detail_plain");
    if ((s.startsWith("{") && s.includes("}")) || (s.startsWith("[") && s.includes("]"))) {
      try {
        JSON.parse(s);
        return t(locale, "detail_json");
      } catch (_) {}
    }
    if (/^[OaidsbDN]:\d+:/m.test(s) || /^[aidsb]:\d+:/m.test(s)) return t(locale, "detail_php");
    return t(locale, "detail_plain");
  }

  $: valueFormat = (() => {
    if (detail?.type !== "string" || detail?.value == null) return null;
    const v = detail.value;
    if (typeof v === "object" && (Array.isArray(v) || (v !== null && v.constructor?.name === "Object"))) return t(locale, "detail_json");
    return getValueFormat(typeof v === "string" ? v : String(v));
  })();

  /** Escape HTML for safe use in {@html} */
  function escapeHtml(s) {
    if (s == null) return "";
    const t = String(s);
    return t
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /**
   * Highlight value with highlight.js. language: 'json' | 'php' | null (plain).
   */
  function highlightValue(code, language) {
    if (code == null) return "";
    const s = String(code);
    if (!language) return escapeHtml(s);
    try {
      return hljs.highlight(s, { language, ignoreIllegals: true }).value;
    } catch (_) {
      return escapeHtml(s);
    }
  }

  $: highlightedValue = (() => {
    if (detail?.value == null) return "";
    if (detail.type === "string") {
      const format = valueFormat;
      let str;
      if (format === "JSON" && typeof detail.value === "object" && detail.value !== null) {
        str = JSON.stringify(detail.value, null, 2);
      } else if (typeof detail.value === "string") {
        str = detail.value;
      } else {
        str = JSON.stringify(detail.value, null, 2);
      }
      const lang = format === t(locale, "detail_json") ? "json" : format === t(locale, "detail_php") ? "php" : null;
      return highlightValue(str, lang);
    }
    return highlightValue(JSON.stringify(detail.value, null, 2), "json");
  })();
</script>

<div class="ui-card h-full flex flex-col">
  <!-- Header -->
  <div class="p-4 border-b border-white/10 flex items-center justify-between">
    <div class="flex items-center gap-3 min-w-0">
      {#if detail}
        <span class={`type-badge ${getTypeBadgeClass(detail.type)}`}>
          {detail.type?.toUpperCase() ?? ""}
        </span>
      {/if}
      <span class="font-mono text-sm text-white truncate">{key}</span>
    </div>
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="p-2 hover:bg-white/10 rounded-lg transition-colors"
        onclick={onRefresh}
        title={t(locale, "detail_refresh")}
      >
        <Icon icon={refreshIcon} width="18" height="18" />
      </button>
      <button
        type="button"
        class="p-2 hover:bg-white/10 rounded-lg transition-colors text-rose-400"
        onclick={onDelete}
        title={t(locale, "detail_delete")}
      >
        <Icon icon={deleteIcon} width="18" height="18" />
      </button>
      <button
        type="button"
        class="p-2 hover:bg-white/10 rounded-lg transition-colors"
        onclick={onClose}
        title={t(locale, "detail_close")}
      >
        <Icon icon={closeIcon} width="18" height="18" />
      </button>
    </div>
  </div>

  <!-- Content -->
  <div class="flex-1 overflow-y-auto p-4">
    {#if !detail}
      <div class="text-center py-8 text-zinc-500">{t(locale, "detail_loading")}</div>
    {:else}
      {#if error}
        <div class="mb-4 text-sm text-rose-400 bg-rose-400/10 border border-rose-400/20 rounded-lg px-3 py-2">
          {error}
        </div>
      {/if}

      <!-- TTL Info -->
      <div class="mb-4 flex items-center gap-2 text-sm">
        <Icon icon={clockIcon} width="16" height="16" class="text-zinc-500" />
        {#if detail.ttl < 0}
          <span class="text-zinc-400">{t(locale, "browse_no_expiry")}</span>
        {:else}
          <span class="text-zinc-400">{t(locale, "detail_expires_in")} </span>
          <span class="text-white font-medium">{formatTTL(detail.ttl)}</span>
          {#if detail.expires_at}
            <span class="text-zinc-500">
              ({new Date(detail.expires_at).toLocaleString()})
            </span>
          {/if}
        {/if}
      </div>

      <!-- Size -->
      <div class="mb-4 text-sm text-zinc-400">
        {t(locale, "detail_size")} <span class="text-white">{detail.size?.toLocaleString() || 0}</span>
        {#if detail.type === "string"}{t(locale, "detail_bytes")}{:else}{t(locale, "detail_items")}{/if}
      </div>

      <!-- Value Editor (for strings) -->
      {#if detail.type === "string"}
        <div class="space-y-4">
          {#if valueFormat}
            <p class="text-xs text-zinc-500">
              {t(locale, "detail_format")} <span class="font-medium text-zinc-400">{valueFormat}</span>
            </p>
          {/if}
          {#if valueFormat === t(locale, "detail_json") || valueFormat === t(locale, "detail_php")}
            <div>
              <label class="block text-sm font-medium text-zinc-400 mb-2">{t(locale, "detail_value_preview")}</label>
              <pre class="hljs w-full px-4 py-3 border border-white/10 rounded-lg font-mono text-sm overflow-x-auto max-h-[280px] overflow-y-auto">{@html highlightedValue}</pre>
            </div>
          {/if}
          <div>
            <label class="block text-sm font-medium text-zinc-400 mb-2">{t(locale, "detail_value")}</label>
            <textarea
              bind:value={editValue}
              rows="10"
              class="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg text-white font-mono text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-400/20 resize-y"
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium text-zinc-400 mb-2">{t(locale, "detail_ttl_label")}</label>
            <input
              type="number"
              bind:value={editTTL}
              min="0"
              class="w-full h-10 px-4 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-400/20"
            />
          </div>

          <button
            type="button"
            class="ui-btn-accent"
            onclick={saveKey}
            disabled={saving}
          >
            <Icon icon={saveIcon} width="16" height="16" />
            {saving ? t(locale, "browse_saving") : t(locale, "detail_save")}
          </button>
        </div>
      {:else}
        <!-- Read-only display for other types (highlight.js) -->
        <div>
          <label class="block text-sm font-medium text-zinc-400 mb-2">{t(locale, "detail_value")}</label>
          <pre class="hljs w-full px-4 py-3 border border-white/10 rounded-lg font-mono text-sm overflow-x-auto max-h-[400px] overflow-y-auto">{@html highlightedValue}</pre>
        </div>
      {/if}
    {/if}
  </div>
</div>
